import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import { useTheme } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useGetPayrollStaffMainDhashboardQuery } from "../../../Api/attoDeskApi";
import { IEmployeePayroll } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsAccessibility(Highcharts);

const PayrollDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [showTable, setShowTable] = useState(false);

  // ✅ Call the API
  const { data: payrollApiData, isLoading } =
    useGetPayrollStaffMainDhashboardQuery({ empid: "" });

  const payrollData = useMemo(() => {
    return (payrollApiData?.data as IEmployeePayroll[]) || [];
  }, [payrollApiData?.data]);

  if (isLoading) return <p>Loading payroll data...</p>;

  // ✅ Overall Totals for Pie Chart
  const totalGross = payrollData.reduce(
    (sum, d) => sum + Number(d.gross_salary || 0),
    0
  );
  const totalOT = payrollData.reduce(
    (sum, d) => sum + Number(d.ot_salary || 0),
    0
  );
  const totalBonus = payrollData.reduce(
    (sum, d) => sum + Number(d.bonus_amount || 0),
    0
  );

  const salaryTrendOptions: Highcharts.Options = {
    chart: { type: "pie", backgroundColor: theme.palette.cardAdd.bgColor },
    title: {
      text: "Overall Payroll Distribution",
      style: {
        fontSize: "0.9375rem",
        color: theme.palette.heading.headingColor,
      },
    },
    tooltip: {
      pointFormat: "<b>${point.y:.2f}</b> ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f} %",
          style: {
            color: theme.palette.heading.headingColor,
            fontSize: "0.75rem",
          },
        },
      },
    },
    legend: {
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading.headingColor,
      },
    },
    series: [
      {
        type: "pie",
        name: "Payroll Share",
        data: [
          { name: "Basic Salary", y: totalGross },
          { name: "Overtime", y: totalOT },
          { name: "Bonus", y: totalBonus },
        ],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div style={{ padding: "5px" }}>
      <HighchartsReact highcharts={Highcharts} options={salaryTrendOptions} />

      {payrollData.length > 0 && (
        <Typography
          onClick={() => setShowTable(!showTable)}
          sx={{ cursor: "pointer", textAlign: "center", mt: 1 }}
          variant="h6"
        >
          {showTable ? "Hide Table View" : "View Table"}
        </Typography>
      )}
      {showTable && (
        <div
          style={{
            marginTop: "15px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "15px",
          }}
        >
          <div
            style={{
              background: theme.palette.cardAdd.bgColor,
              padding: "15px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Gross Salary</Typography>
            <Typography className={classes.h5} mt={"2px"}>
              ${totalGross.toFixed(2)}
            </Typography>
          </div>

          <div
            style={{
              background: theme.palette.cardAdd.bgColor,
              padding: "15px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>OT Salary</Typography>
            <Typography className={classes.h5} mt={"2px"}>
              ${totalOT.toFixed(2)}
            </Typography>
          </div>

          <div
            style={{
              background: theme.palette.cardAdd.bgColor,
              padding: "15px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Bonus</Typography>
            <Typography className={classes.h5} mt={"2px"}>
              ${totalBonus.toFixed(2)}
            </Typography>
          </div>

          <div
            style={{
              background: theme.palette.cardAdd.bgColor,
              padding: "15px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Total Salary</Typography>
            <Typography className={classes.h5} mt={"2px"}>
              ${(totalGross + totalOT + totalBonus).toFixed(2)}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollDashboard;
